import { useEffect, useRef, useState } from "react";
import InputCheckBox from "./InputCheckBox";
import { useFormContext } from "react-hook-form";
import SelectCoordinate from "./select/SelectCoordinate";

import { tr } from "../i18n";
const InputWorkingDaysTime = ({
  defaultValue = null,
  isClear = false,
  showLocation = true,
}) => {
  const days = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  const {
    register,
    formState: { errors, isSubmitSuccessful },
    setValue,
    watch,
    clearErrors,
    getValues,
    reset,
  } = useFormContext();

  const [checkedDays, setCheckedDays] = useState([]);
  const schedule = watch("schedule");

  const initializedRef = useRef(false);
  const prevKeyRef = useRef("");

  // --- Generate a stable key from schedules
  const schedulesKey = JSON.stringify(defaultValue?.schedules || []);

  // --- Build schedule object + checked days from defaultValue
  const buildScheduleFromDefault = () => {
    const checked = [];

    const result = days.map((day, idx) => {
      const match = defaultValue.schedules.find((s) => s.schedule_day === day);

      const isChecked = match
        ? (match.is_checked ?? true) // Important logic for dataset without is_checked
        : false;

      if (isChecked) checked.push(idx);

      return {
        schedule_id: match?.schedule_id || null,
        day,
        time_in: match?.schedule_time_in || "",
        time_out: match?.schedule_time_out || "",
        coordinate: match?.map_id || null,
        is_checked: isChecked,
      };
    });

    return { result, checked };
  };

  // --- Sync initializer (runs before first render)
  if (!initializedRef.current && defaultValue?.schedules) {
    const { result, checked } = buildScheduleFromDefault();

    setCheckedDays(checked);
    setValue("schedule", result, {
      shouldValidate: false,
      shouldDirty: false,
      shouldTouch: false,
    });

    initializedRef.current = true;
    prevKeyRef.current = schedulesKey;
  }

  // --- Effect for when defaultValue changes after load
  useEffect(() => {
    if (!defaultValue?.schedules) return;

    // If schedules content is unchanged, do nothing
    if (initializedRef.current && prevKeyRef.current === schedulesKey) return;

    const { result, checked } = buildScheduleFromDefault();

    setCheckedDays(checked);
    setValue("schedule", result, {
      shouldValidate: false,
      shouldDirty: false,
      shouldTouch: false,
    });

    initializedRef.current = true;
    prevKeyRef.current = schedulesKey;
  }, [schedulesKey, defaultValue?.schedules, setValue]);

  // --- Checkbox handler
  const handleCheckBoxChange = (day, isChecked, index) => {
    const newChecked = isChecked
      ? [...checkedDays, index]
      : checkedDays.filter((i) => i !== index);

    setCheckedDays(newChecked);

    const current = getValues("schedule") || [];

    const safeBase =
      current.length >= days.length
        ? current
        : days.map((d) => ({
            schedule_id: null,
            day: d,
            time_in: "",
            time_out: "",
            coordinate: null,
            is_checked: false,
          }));

    const updated = safeBase.map((item, idx) => {
      const isActive = newChecked.includes(idx);

      return {
        ...item,
        day: days[idx],
        is_checked: isActive,
        // Clear values jika checkbox tidak aktif
        time_in: isActive ? item.time_in : "",
        time_out: isActive ? item.time_out : "",
        coordinate: isActive ? item.coordinate : null,
      };
    });

    setValue("schedule", updated, {
      shouldValidate: false,
      shouldDirty: true,
      shouldTouch: false,
    });
  };

  const hasMounted = useRef(false);

  useEffect(() => {
    if (isSubmitSuccessful) {
      const hasDefault = defaultValue?.schedules?.length > 0;

      // Jika ada default schedule & isClear = false → LEWATI reset
      if (hasDefault && !isClear) return;

      // Jika tidak ada defaultValue atau isClear true → reset
      reset({
        schedule: days.map((d) => ({
          day: d,
          time_in: "",
          time_out: "",
          coordinate: null,
          is_checked: false,
        })),
      });

      setCheckedDays([]);
    }
  }, [isSubmitSuccessful, isClear, defaultValue]);

  // --- Validation
  const validateCheckbox = () =>
    checkedDays.length === 0 ? "At least choose 1 day." : true;

  return (
    <table className="table schedule-table mb-0">
      <thead>
        <tr>
          <th
            style={{ width: showLocation ? "20%" : "25%", textAlign: "center" }}
          >
            {tr("attendance:days_2")}
          </th>
          <th
            style={{ width: showLocation ? "40%" : "65%", textAlign: "center" }}
          >
            {tr("attendance:time_in_out")}
          </th>
          {showLocation && (
            <th style={{ width: "40%", textAlign: "center" }}>{tr("shared:location")}</th>
          )}
        </tr>
      </thead>

      <tbody>
        {days.map((day, index) => {
          const isActive = checkedDays.includes(index);
          const current = schedule?.[index] || {};

          return (
            <tr key={index} className={isActive ? "active-row" : ""}>
              <td>
                <InputCheckBox
                  key={`${index}-${isActive}`}
                  text={day}
                  checked={isActive}
                  onCheckBoxChange={(isChecked) =>
                    handleCheckBoxChange(day, isChecked, index)
                  }
                  name={`schedule[${index}].day`}
                  rules={{ validate: validateCheckbox }}
                  className={`${
                    errors.schedule && checkedDays.length === 0
                      ? "is-invalid"
                      : ""
                  } ${isActive ? "active" : ""}`}
                />
              </td>

              {/* TIME */}
              <td className="text-center">
                <div className="d-flex justify-content-center gap-2 align-items-center">
                  <input
                    {...register(`schedule[${index}].time_in`, {
                      required: isActive ? tr("attendance:this_input_is_required") : false,
                    })}
                    type="time"
                    className={`form-control input-time ${
                      isActive && errors?.schedule?.[index]?.time_in
                        ? "is-invalid"
                        : ""
                    }`}
                    disabled={!isActive}
                    defaultValue={current?.time_in || ""}
                  />

                  <span className="text-muted">—</span>

                  <input
                    {...register(`schedule[${index}].time_out`, {
                      required: isActive ? tr("attendance:this_input_is_required") : false,
                    })}
                    type="time"
                    className={`form-control input-time ${
                      isActive && errors?.schedule?.[index]?.time_out
                        ? "is-invalid"
                        : ""
                    }`}
                    disabled={!isActive}
                    defaultValue={current?.time_out || ""}
                  />
                </div>
              </td>

              {showLocation && (
                <td className="text-left">
                  <SelectCoordinate
                    key={`${index}-${current?.coordinate || ""}-${isActive}`} // tambahkan isActive + coordinate
                    name={`schedule[${index}].coordinate`}
                    rules={null}
                    disabled={!isActive}
                    defaultValue={current?.coordinate || null}
                  />
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default InputWorkingDaysTime;
